/**
 * AdminFlaggedTicketsTab — review queue for operator/cashier-flagged lottery
 * tickets. Admin can Dismiss (clear flag, no money moves) or Void (refund
 * the wager and mark the ticket void; only allowed for pending tickets).
 *
 * Backed by:
 *   GET  /api/admin/lottery/flagged-tickets?flag_status=pending|dismissed|voided|all
 *   POST /api/admin/lottery/flagged-tickets/{id}/review  body: { action, note }
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { RefreshCw, Loader2, Check, Ban, ShieldAlert, Camera } from 'lucide-react';
import { FLAG_REASON_SHORT as REASON_LABELS } from '../../constants/lotteryFlagReasons';
import { asUTC } from '../../lib/ctTime';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const STATUS_TABS = [
    { key: 'pending',   label: 'Pending review' },
    { key: 'dismissed', label: 'Dismissed' },
    { key: 'voided',    label: 'Voided' },
];

const AdminFlaggedTicketsTab = () => {
    const [activeStatus, setActiveStatus] = useState('pending');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [scan, setScan] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/lottery/flagged-tickets?flag_status=${activeStatus}&limit=200`,
                { withCredentials: true });
            setRows(data.rows || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [activeStatus]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="admin-flagged-tickets">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-rose-400" />
                        Flagged lottery tickets
                    </h2>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        Tickets flagged by operators or cashiers. Dismiss to clear; Void to refund the
                        wager (pending tickets only).
                    </p>
                </div>
                <Button size="sm" variant="outline" onClick={load}
                    className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300"
                    data-testid="flagged-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            <div className="flex items-center gap-1 border-b border-zinc-800">
                {STATUS_TABS.map(t => {
                    const on = activeStatus === t.key;
                    return (
                        <button key={t.key}
                            onClick={() => setActiveStatus(t.key)}
                            className={`relative px-4 py-2 text-sm font-medium transition
                                ${on ? 'text-rose-300' : 'text-zinc-500 hover:text-zinc-300'}`}
                            data-testid={`flagged-tab-${t.key}`}>
                            {t.label}
                            {on && <span className="absolute left-2 right-2 bottom-0 h-[2px] bg-rose-400 rounded-full" />}
                        </button>
                    );
                })}
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-900/30 overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Flagged</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Reason</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Player / Location</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Game</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Numbers</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Amount</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Ticket status</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow className="border-zinc-800">
                                    <TableCell colSpan={8} className="text-center text-zinc-500 py-6">
                                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && rows.length === 0 && (
                                <TableRow className="border-zinc-800">
                                    <TableCell colSpan={8} className="text-center text-zinc-500 py-6 text-sm">
                                        No flagged tickets in this bucket.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && rows.map((r, i) => (
                                <TableRow key={r.id} className="border-zinc-800 hover:bg-zinc-900/50"
                                    data-testid={`flagged-row-${i}`}>
                                    <TableCell className="text-xs text-zinc-400 whitespace-nowrap">
                                        {r.flagged_at ? asUTC(r.flagged_at).toLocaleString(undefined, { timeZone: 'America/Chicago' }) : '—'}
                                        <div className="text-[10px] text-zinc-600">
                                            by {r.flagged_by_name} ({r.flagged_by_role})
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge className="bg-rose-500/20 text-rose-300 border border-rose-500/30">
                                            {REASON_LABELS[r.flag_reason] || r.flag_reason}
                                        </Badge>
                                        {r.flag_note && <div className="text-[11px] text-zinc-500 mt-1 max-w-[220px] truncate">{r.flag_note}</div>}
                                    </TableCell>
                                    <TableCell className="text-zinc-100">
                                        <div className="font-medium">{r.user_name}</div>
                                        <div className="text-[11px] text-zinc-500">{r.operator_name || '—'}</div>
                                    </TableCell>
                                    <TableCell className="text-zinc-300 text-sm">
                                        {r.bet_type === 'big3'
                                            ? <span className="text-amber-300 font-semibold">Big 3 · {r.state}</span>
                                            : <span>{r.state} P{r.pick_type} <span className="text-zinc-500">· {r.bet_type}</span></span>}
                                        <div className="text-[11px] text-zinc-500">{r.draw_date} · {r.drawing_time}</div>
                                    </TableCell>
                                    <TableCell className="font-mono text-zinc-100 tracking-wider">{(r.numbers || []).join('-')}</TableCell>
                                    <TableCell className="text-right font-mono text-zinc-300">{fmt(r.amount)}</TableCell>
                                    <TableCell>
                                        <span className={`text-xs px-2 py-0.5 rounded border
                                            ${r.status === 'pending' ? 'border-amber-500/30 text-amber-300 bg-amber-500/10'
                                             : r.status === 'won'    ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10'
                                             : r.status === 'void'   ? 'border-zinc-500/30 text-zinc-400 bg-zinc-500/10'
                                             : 'border-rose-500/30 text-rose-300 bg-rose-500/10'}`}>
                                            {r.status}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <Button size="sm" variant="outline" onClick={() => { setScan(null); setSelected(r); }}
                                            className="h-7 border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 text-xs"
                                            data-testid={`review-btn-${i}`}>
                                            Review
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <ReviewDialog ticket={selected} scan={scan} setScan={setScan}
                onClose={() => setSelected(null)}
                onDone={() => { setSelected(null); load(); }}
            />
        </div>
    );
};

const ReviewDialog = ({ ticket, scan, setScan, onClose, onDone }) => {
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        setNote('');
        setScan(null);
        if (ticket?.scan_id) {
            axios.get(`${API}/admin/lottery/scans/${ticket.scan_id}`, { withCredentials: true })
                .then(({ data }) => setScan(data))
                .catch(() => {/* admin route may not exist for legacy — try operator route fallback */
                    // For consistency we let admin reuse the operator route which is scoped — admins can call any endpoint
                });
        }
    }, [ticket?.scan_id, setScan]);

    if (!ticket) return null;

    const review = async (action) => {
        setBusy(action);
        try {
            const { data } = await axios.post(
                `${API}/admin/lottery/flagged-tickets/${ticket.id}/review`,
                { action, note: note.trim() || null },
                { withCredentials: true });
            if (action === 'void' && data.refunded > 0) {
                toast.success(`Ticket voided — refunded ${fmt(data.refunded)} to player`);
            } else {
                toast.success(`Flag ${action === 'dismiss' ? 'dismissed' : 'voided'}`);
            }
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || `Could not ${action}`);
        } finally {
            setBusy(null);
        }
    };

    const canVoid = ticket.status === 'pending' && ticket.flag_status === 'pending';

    return (
        <Dialog open={!!ticket} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="bg-[#121212] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="flagged-review-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white">Review flagged ticket</DialogTitle>
                </DialogHeader>

                <div className="space-y-3 py-2 text-sm">
                    <Row label="Player" value={ticket.user_name} />
                    <Row label="Location" value={ticket.operator_name || '—'} />
                    <Row label="Game" value={ticket.bet_type === 'big3'
                        ? `Big 3 — ${ticket.state}`
                        : `${ticket.state} Pick ${ticket.pick_type} (${ticket.bet_type})`} />
                    <Row label="Numbers" value={(ticket.numbers || []).join('-')} mono />
                    <Row label="Drawing" value={`${ticket.draw_date} · ${ticket.drawing_time}`} />
                    <Row label="Amount" value={fmt(ticket.amount)} mono />
                    <Row label="Ticket status" value={ticket.status} />

                    <div className="bg-rose-950/30 border border-rose-500/30 rounded p-3 space-y-1">
                        <div className="text-xs uppercase tracking-wider text-rose-200 font-semibold">Flag detail</div>
                        <div className="text-zinc-300 text-sm"><span className="text-zinc-500">Reason:</span> {REASON_LABELS[ticket.flag_reason] || ticket.flag_reason}</div>
                        {ticket.flag_note && <div className="text-zinc-300 text-sm"><span className="text-zinc-500">Note:</span> {ticket.flag_note}</div>}
                        <div className="text-zinc-500 text-xs">
                            By {ticket.flagged_by_name} ({ticket.flagged_by_role})
                            {ticket.flagged_at && ` · ${asUTC(ticket.flagged_at).toLocaleString(undefined, { timeZone: 'America/Chicago' })}`}
                        </div>
                    </div>

                    {ticket.scan_id && scan && (
                        <div className="bg-zinc-950 border border-zinc-800 rounded p-3 space-y-2">
                            <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold flex items-center gap-2">
                                <Camera className="w-3.5 h-3.5" /> Original scan
                            </div>
                            <img src={`data:${scan.mime_type};base64,${scan.image_base64}`}
                                alt="scan"
                                className="w-full max-h-[360px] object-contain rounded border border-zinc-700"
                                data-testid="review-scan-image" />
                        </div>
                    )}

                    <div className="pt-3 border-t border-zinc-800">
                        <label className="text-xs text-zinc-400 uppercase tracking-wider">Review note (optional)</label>
                        <Input value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="Why you're dismissing or voiding"
                            className="mt-1 bg-zinc-950 border-zinc-700 text-zinc-100"
                            data-testid="review-note" />
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                        <Button onClick={() => review('dismiss')} disabled={!!busy}
                            className="bg-zinc-700 hover:bg-zinc-600 text-white"
                            data-testid="review-dismiss-btn">
                            {busy === 'dismiss' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                            Dismiss
                        </Button>
                        <Button onClick={() => review('void')} disabled={!!busy || !canVoid}
                            title={canVoid ? '' : 'Void only available on pending tickets'}
                            className="bg-rose-500 hover:bg-rose-400 text-white disabled:opacity-40"
                            data-testid="review-void-btn">
                            {busy === 'void' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Ban className="w-4 h-4 mr-1" />}
                            Void + refund wager
                        </Button>
                        <Button variant="outline" onClick={onClose}
                            className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">
                            Close
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Row = ({ label, value, mono }) => (
    <div className="flex justify-between gap-4 py-0.5">
        <span className="text-zinc-500 uppercase tracking-wider text-[11px] pt-0.5">{label}</span>
        <span className={`text-zinc-100 text-right ${mono ? 'font-mono tracking-wider' : ''}`}>{value}</span>
    </div>
);

export default AdminFlaggedTicketsTab;
